/**
 * waitForInitialSync — waits for Dexie Cloud's initial sync to complete.
 *
 * Subscribes to `db.cloud.syncState` and resolves when phase reaches
 * "in-sync" (all server data downloaded). Falls back to timeout for
 * offline/slow network scenarios.
 *
 * Used by DatabaseProvider to defer populateIfNeeded() until after
 * the initial pull completes — preventing duplicate tutorial projects
 * when an existing user logs in on a new device.
 */

/** An object with a Dexie Cloud `syncState` observable */
interface HasCloudSyncState {
  cloud: {
    syncState: {
      value: { phase: string; status: string } | undefined
      subscribe: (observer: (state: { phase: string; status: string }) => void) => { unsubscribe: () => void }
    }
  }
}

// Dexie Cloud phases that mean "pull is done" (data is available locally):
// - "in-sync": full sync completed
// - "error": sync failed but we should still proceed (offline data may exist)
const READY_PHASES = new Set(["in-sync", "error", "offline"])

/**
 * Wait for the initial Dexie Cloud sync to reach a ready state.
 *
 * @param db - Dexie instance with cloud addon
 * @param timeoutMs - Maximum wait time (default: 300s, matches InitialSyncLoader MAX_DISPLAY_MS)
 * @returns `true` if sync completed, `false` if timed out
 */
export function waitForInitialSync(
  db: HasCloudSyncState,
  timeoutMs = 300_000,
): Promise<boolean> {
  return new Promise((resolve) => {
    // Check if already in a ready state (e.g., returning user with cached data)
    const current = db.cloud.syncState.value
    if (current && READY_PHASES.has(current.phase)) {
      resolve(true)
      return
    }

    let settled = false
    const settle = (result: boolean) => {
      if (settled) return
      settled = true
      sub.unsubscribe()
      clearTimeout(timer)
      resolve(result)
    }

    const sub = db.cloud.syncState.subscribe(
      (state: { phase: string; status: string }) => {
        if (READY_PHASES.has(state.phase)) {
          settle(true)
        }
      },
    )

    const timer = setTimeout(() => {
      settle(false)
    }, timeoutMs)
  })
}
